import { useContext } from 'react';
import { formatDateToFriendlyString } from '@/util/date-formatters';
import LabelValue from 'components/LabelValue';
import TransactionChart from 'components/TransactionChart';
import { UserSessionContext } from 'providers/UserSessionProvider';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { getSoCChartData } from 'util/get-soc-chart-data';
import IntlMessages from 'util/IntlMessages';

const TransactionInfo = ({ transaction, transactionConnector }) => {
  const { timezone } = useContext(UserSessionContext);
  const soh = transaction.soh ? parseInt(transaction.soh) : null;

  const { chartData, startColor, stopColor } = getSoCChartData(
    transaction.soc_start,
    transaction.soc_stop
  );

  const userStart = transaction?.user_start
    ? transaction?.user_start?.email
    : transaction?.anonymous_transaction_request?.email;

  const userStop = transaction?.timestamp_stop
    ? transaction?.user_stop
      ? transaction?.user_stop?.email
      : transaction?.anonymous_transaction_request?.email
    : '- - -';

  const registeredUser = transaction?.anonymous_transaction_request ? 'No' : 'Sí';

  return (
    <div className="transaction-info-container">
      {transactionConnector && transaction?.id ? (
        <LabelValue
          label={<IntlMessages id="components.charge-point-details.transacion-id" />}
          value={transaction?.id}
        />
      ) : null}
      {transactionConnector && transactionConnector.connector_id ? (
        <LabelValue
          label={<IntlMessages id="components.charge-point-details.connector-number" />}
          value={transactionConnector.connector_id}
        />
      ) : null}
      {transactionConnector && transactionConnector.verbose_name ? (
        <LabelValue
          label={<IntlMessages id="components.charge-point-details.alias" />}
          value={transactionConnector.verbose_name}
        />
      ) : null}
      <LabelValue
        label={<IntlMessages id="components.charge-point-details.start" />}
        value={
          transaction.timestamp_start
            ? formatDateToFriendlyString(transaction.timestamp_start, timezone)
            : '- - -'
        }
      />
      <LabelValue
        label={<IntlMessages id="components.charge-point-details.user-start" />}
        value={userStart || '---'}
      />
      <LabelValue
        label={<IntlMessages id="components.charge-point-details.anonymous-user" />}
        value={registeredUser}
      />
      <LabelValue
        label={<IntlMessages id="components.charge-point-details.end" />}
        value={
          transaction.timestamp_stop
            ? formatDateToFriendlyString(transaction.timestamp_stop, timezone)
            : '- - -'
        }
      />
      <LabelValue
        label={<IntlMessages id="components.charge-point-details.user-end" />}
        value={userStop || '- - -'}
      />
      <LabelValue
        label={<IntlMessages id="components.charge-point-details.charged-energy" />}
        alert={
          parseFloat(transaction.total_energy) < 0
            ? 'Es posible que este dato no sea correcto.'
            : undefined
        }
        value={
          transaction?.total_energy
            ? `${(transaction.total_energy / 1000).toFixed(2)} kWh`
            : '- - -'
        }
      />
      {transaction.vehicle ? (
        <LabelValue
          label={<IntlMessages id="components.charge-point-details.vehicle" />}
          value={
            transaction.vehicle.verbose_name !== transaction.vehicle.number_plate
              ? `${transaction.vehicle.verbose_name} (${transaction.vehicle.number_plate})`
              : transaction.vehicle.verbose_name
          }
        />
      ) : null}
      {transaction.soc_start && transaction.soc_stop ? (
        <>
          <h6 className="subtitle mb-2">
            <IntlMessages id="components.charge-point-details.transaction-soc" />
          </h6>
          <div className="d-flex align-items-center justify-content-center mb-2">
            <div style={{ height: '48px', width: '48px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    innerRadius={8}
                    outerRadius={15}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="d-flex align-items-center justify-content-center">
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center">
                  <div
                    className="mr-1 rounded-circle"
                    style={{
                      backgroundColor: startColor,
                      width: '10px',
                      height: '10px',
                    }}
                  ></div>
                  <span className="font-size-12 mr-2">SoC inicial</span>
                  <b>{`${Math.round(transaction.soc_start)}%`}</b>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="mr-1 rounded-circle"
                    style={{
                      backgroundColor: stopColor,
                      width: '10px',
                      height: '10px',
                    }}
                  ></div>
                  <span className="font-size-12 mr-2">SoC final</span>
                  <b>{`${Math.round(transaction.soc_stop)}%`}</b>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
      {soh ? (
        <LabelValue
          label={<IntlMessages id="components.charge-point-details.vehicle-soh" />}
          value={`${Math.round(soh * 100)}%`}
        />
      ) : null}
      <TransactionChart transaction={transaction} />
    </div>
  );
};

export default TransactionInfo;
